import {
  runBulk,
  type BulkAction,
} from "@/components/molecules/tables/bulk-actions";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import { useDeleteCustomer } from "@/features/customers/hooks/use-customers";
import type { Customer } from "@/features/customers/types";
import { useTranslation } from "@/features/utils/i18n";
import { TrashIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

export const useCustomersBulkActions = (): BulkAction<Customer>[] => {
  const { t } = useTranslation("customers");
  const { company: clientId } = useParams<{ company: string }>();
  const isOwner = useHasRole("OWNER");
  const deleteCustomer = useDeleteCustomer();

  return useMemo(() => {
    if (!isOwner || !clientId) return [];

    return [
      {
        key: "delete",
        label: t("actions.deleteSelected"),
        icon: <TrashIcon className="h-4 w-4" />,
        variant: "destructive",
        confirm: (rows) =>
          t("messages.bulkDeleteConfirm", { count: rows.length }),
        onAction: async (rows) => {
          await runBulk(
            rows,
            (customer) =>
              deleteCustomer.mutateAsync({ clientId, customerId: customer.id }),
            {
              success: (count) => t("messages.bulkDeleteSuccess", { count }),
              error: (count) => t("messages.bulkDeleteError", { count }),
            },
          );
        },
      },
    ];
  }, [isOwner, clientId, t, deleteCustomer]);
};
